import { Injectable } from '@angular/core';
import { delay, of, type Observable } from 'rxjs';

import type {
  EmailAnalyticsCharts,
  EmailAnalyticsChartsParams,
  EmailAnalyticsClient,
} from '../tokens/email-analytics-client.token';

const TIME_CATEGORIES = ['1/1', '1/2', '1/3', '1/4', '1/5', '1/6', '1/7', '1/8', '1/9', '1/10', '1/11', '1/12', '1/13'];

const EMPLOYEE_METRIC_LINE_DATA = [1.2, 1.3, 1.4, 1.6, 2.1, 3, 4.2, 5.3, 6.2, 6.9, 7, 7.2, 8.2];

/** Peer average box [min, Q1, median, Q3, max] per time point - trending up like employee metric */
function buildTimeSeriesBoxData(): number[][] {
  return TIME_CATEGORIES.map((_, i) => {
    const m = EMPLOYEE_METRIC_LINE_DATA[i];
    const spread = 1.2;
    return [
      Math.max(0, m - spread),
      m - spread * 0.5,
      m,
      m + spread * 0.5,
      Math.min(10, m + spread),
    ];
  });
}

function buildTimeSeriesChart(
  title: string,
  subtitle: string
): EmailAnalyticsCharts['medRemovalRisk'] {
  return {
    type: 'boxplot',
    title,
    subtitle,
    theme: 'light',
    xAxis: {
      type: 'category',
      name: '',
      splitLine: { show: false },
      axisLine: { show: true, lineStyle: { color: '#000000' } },
    },
    yAxis: {
      type: 'value',
      name: 'Risk Score',
      nameLocation: 'end',
      nameGap: 15,
      nameShow: true,
      nameTextStyle: { color: 'rgba(0, 0, 0, 0.80)', fontSize: 12 },
      min: 0,
      max: 10,
      splitNumber: 10,
      splitLine: {
        show: true,
        lineStyle: { color: '#DADFE5', type: 'solid' },
      },
    },
    categories: TIME_CATEGORIES,
    boxplotSeries: [
      {
        name: 'Peer Average',
        data: buildTimeSeriesBoxData(),
        color: '#d9f4ff',
        itemStyle: {
          color: '#d9f4ff',
          borderColor: '#6C6C6D',
          borderWidth: 1.5,
        },
        emphasis: { scale: false, itemStyle: { color: '#D7F2FE' } },
      },
    ],
    lineSeries: [
      {
        name: 'Employee Metric',
        data: [...EMPLOYEE_METRIC_LINE_DATA],
        color: '#CD0000',
        showSymbol: true,
        lineStyle: { width: 2, color: '#CD0000' },
        itemStyle: { color: '#E8856B', borderColor: '#E8856B', borderWidth: 0 },
      },
    ],
    legend: { show: false },
    tooltip: { trigger: 'axis' },
    customOptions: {
      title: {
        show: true,
        itemGap: 4,
        textStyle: {
          color: 'rgba(0, 0, 0, 0.80)',
          fontFamily: 'Inter',
          fontSize: 24,
          fontWeight: 900,
          lineHeight: 32,
        },
        subtextStyle: {
          color: 'rgba(0, 0, 0, 0.80)',
          fontFamily: 'Inter',
          fontSize: 20,
          fontWeight: 700,
          lineHeight: 28,
        },
      },
      legend: {
        show: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 8,
        itemGap: 24,
        itemWidth: 14,
        itemHeight: 10,
        data: [
          { name: 'Peer Average', itemStyle: { color: '#D7F2FE' }, symbol: 'rect' },
          { name: 'Employee Metric', icon: 'circle', itemStyle: { color: '#E8856B' }, symbol: 'circle', symbolSize: 14, itemWidth: 18, itemHeight: 14 },
        ],
        textStyle: { color: 'rgba(0, 0, 0, 0.80)', fontSize: 12 },
      },
      grid: {
        top: 100,
        left: '3%',
        right: '3%',
        bottom: 72,
        containLabel: true,
      },
    },
  };
}

@Injectable({ providedIn: 'root' })
export class EmailAnalyticsMockService implements EmailAnalyticsClient {
  getCharts(params: EmailAnalyticsChartsParams): Observable<EmailAnalyticsCharts> {
    const subtitle = `${params.medicationName} - Incident #${params.incidentNumber}`;
    const charts: EmailAnalyticsCharts = {
      medRemovalRisk: buildTimeSeriesChart('Med Removal Risk Scores', subtitle),
      wasteTransactionsRisk: buildTimeSeriesChart('Waste Transactions Risk Scores', subtitle),
      chartedRisk: buildTimeSeriesChart('Charted Risk Scores', subtitle),
      unaccountedVariancesRisk: buildTimeSeriesChart('Unaccounted Variances Risk Scores', subtitle),
      employeeRiskPerAudit: {
        type: 'boxplot',
        title: 'Employee Risk Scores Per Audit',
        subtitle,
        theme: 'light',
        xAxis: {
          type: 'category',
          name: '',
          splitLine: { show: false },
          axisLine: { show: true, lineStyle: { color: '#000000' } },
        },
        yAxis: {
          type: 'value',
          name: 'Risk Score',
          nameLocation: 'end',
          nameGap: 15,
          nameShow: true,
          nameTextStyle: { color: 'rgba(0, 0, 0, 0.80)', fontSize: 12 },
          min: 0,
          max: 10,
          splitNumber: 10,
          splitLine: {
            show: true,
            lineStyle: { color: '#DADFE5', type: 'solid' },
          },
        },
        categories: ['Med Removals', 'Waste Transactions', 'Charted', 'Unaccounted Variances'],
        boxplotSeries: [
          {
            name: 'Peer Average',
            data: [
              [0.9, 1.2, 3.4, 4.9, 5.6],
              [3.1, 3.2, 4.2, 4.9, 5.0],
              [0.8, 0.9, 2.2, 4.6, 5.5],
              [2.1, 2.9, 4.3, 5.9, 7.1],
            ],
            color: '#D7F2FE',
            itemStyle: {
              color: '#D7F2FE',
              borderColor: '#000000',
              borderWidth: 1,
            },
            medianLineStyle: {
              color: '#39A8DC',
              width: 3,
              borderWidth: 3,
              borderColor: '#39A8DC',
            },
            emphasis: {
              scale: false,
              itemStyle: { color: '#D7F2FE', borderWidth: 3, borderColor: '#39A8DC' },
            },
          },
        ],
        scatterSeries: [
          {
            name: 'Employee Metric',
            data: [[0, 5.6], [1, 4.0], [2, 1.8], [3, 4.8]],
            color: '#ef4444',
            symbolSize: 12,
            itemStyle: { borderWidth: 2, borderColor: '#fff' },
          },
        ],
        legend: { show: false },
        tooltip: { trigger: 'item' },
        customOptions: {
          title: {
            show: true,
            itemGap: 4,
            textStyle: {
              color: 'rgba(0, 0, 0, 0.80)',
              fontFamily: 'Inter',
              fontSize: 24,
              fontWeight: 900,
              lineHeight: 32,
            },
            subtextStyle: {
              color: 'rgba(0, 0, 0, 0.80)',
              fontFamily: 'Inter',
              fontSize: 20,
              fontWeight: 700,
              lineHeight: 28,
            },
          },
          legend: {
            show: true,
            orient: 'horizontal',
            left: 'center',
            bottom: 8, 
            itemGap: 24,
            itemWidth: 14,
            itemHeight: 10,
            data: [
              { name: 'Peer Average', itemStyle: { color: '#D7F2FE' }, symbol: 'rect' },
              { name: 'Employee Metric', itemStyle: { color: '#ef4444' }, symbol: 'circle' },
            ],
            textStyle: {
              color: 'rgba(0, 0, 0, 0.80)',
              fontSize: 12,
            },
          },
          grid: {
            top: 100,
            left: '3%',
            right: '3%',
            bottom: 72,
            containLabel: true,
          },
        },
      },
    };
    return of(charts satisfies EmailAnalyticsCharts).pipe(delay(300));
  }
}
